import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Phone, Mail, MapPin, CheckCircle, Loader2, X, Plus, Trash2, User, Navigation, MessageSquare, Send, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import {
    WeeklyHours, DAY_LABELS, defaultWeeklyHours, PX_PER_HOUR, timeToMinutes, minutesToTime,
    formatDateInput, formatDateHuman, contactLabel, dayKeyForDate, dayShortLabel, packAppointments,
    EmailLogEntry, EMAIL_TYPE_LABELS,
} from '../PrestationPlanning/prestationPlanningShared';

const api = (path: string, options?: RequestInit) => {
    const token = localStorage.getItem('token');
    return fetch(`/api${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options?.headers }
    }).then((r) => r.json());
};

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);

interface CatalogItem { id: string; label: string; unit_price_ttc: number; tax_rate: number; show_tva?: boolean; }
interface ExtraLine { label: string; unit_price_ttc: number; tax_rate: number; quantity: number; }
interface Employee { id: string; first_name: string; last_name: string; }

interface Appointment {
    id: string;
    crm_first_name?: string;
    crm_last_name?: string;
    crm_company_name?: string;
    crm_phone?: string;
    crm_email?: string;
    crm_address?: string;
    crm_postal_code?: string;
    crm_city?: string;
    location_type?: 'domicile' | 'entreprise';
    employee_ids: string[];
    prestation_label: string;
    quantity: number;
    unit_price_ttc: number;
    tax_rate: number;
    catalog_items?: { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number }[];
    computed_duration_minutes: number;
    appointment_date: string;
    start_time: string;
    status: string;
    payment_status: string;
    payment_method?: string;
    payment_breakdown?: { method: string; amount: number }[];
    completed_at?: string;
    total_amount_ttc?: number;
    amount_already_paid?: number;
    facture_id?: string;
    notes: string;
}

const employeeNamesLabel = (appt: Appointment, employees: Employee[]) => {
    return (appt.employee_ids || [])
        .map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');
};

const pillClass = (active: boolean) =>
    `flex-1 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`;

function DetailModal({ appt, onClose, onStartCompletion }: { appt: Appointment; onClose: () => void; onStartCompletion: () => void; }) {
    const [tab, setTab] = useState<'details' | 'emails'>('details');
    const [emails, setEmails] = useState<EmailLogEntry[]>([]);
    const [loadingEmails, setLoadingEmails] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [sendingReminder, setSendingReminder] = useState(false);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [previewEmail, setPreviewEmail] = useState<EmailLogEntry | null>(null);
    const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);
    const [resendEmailResult, setResendEmailResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);

    const endTime = minutesToTime(timeToMinutes(appt.start_time) + Math.round(appt.computed_duration_minutes));
    const fullAddress = [appt.crm_address, [appt.crm_postal_code, appt.crm_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;
    const isClosed = appt.status === 'termine';
    const remainingDue = Math.max(0, Number(appt.total_amount_ttc || 0) - Number(appt.amount_already_paid || 0));
    // Une fois clôturée ET payée intégralement, plus rien à notifier — la "date passée" n'est plus le
    // critère : une prestation clôturée mais pas encore payée en intégralité doit au contraire pouvoir
    // être relancée pour le paiement, peu importe la date.
    const isFullyPaid = isClosed && Number(appt.total_amount_ttc || 0) > 0 && remainingDue <= 0;
    const isClosedUnpaid = isClosed && remainingDue > 0;
    const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

    const loadEmails = () => {
        setLoadingEmails(true);
        api(`/employe/prestation-planning/appointments/${appt.id}/emails`)
            .then((d) => setEmails(Array.isArray(d) ? d : []))
            .finally(() => setLoadingEmails(false));
    };

    useEffect(() => {
        if (tab !== 'emails') return;
        loadEmails();
    }, [tab]);

    const handleResend = async () => {
        setResending(true);
        setResendMessage(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/resend-confirmation`, { method: 'POST' });
            if (res?.success) {
                setResendMessage({ type: 'success', text: 'Email envoyé.' });
                if (tab === 'emails') loadEmails();
            } else {
                setResendMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setResending(false);
        }
    };

    const handleResendEmail = async (emailId: string) => {
        setResendingEmailId(emailId);
        setResendEmailResult(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/emails/${emailId}/resend`, { method: 'POST' });
            if (res?.success) {
                setResendEmailResult({ id: emailId, ok: true, msg: 'Envoyé.' });
                loadEmails();
            } else {
                setResendEmailResult({ id: emailId, ok: false, msg: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendEmailResult({ id: emailId, ok: false, msg: 'Erreur lors de l\'envoi.' });
        } finally {
            setResendingEmailId(null);
        }
    };

    const handleSendReminder = async () => {
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/send-reminder`, { method: 'POST' });
            if (res?.success) {
                setReminderMessage({ type: 'success', text: res?.warning || 'Rappel envoyé.' });
                if (tab === 'emails') loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    // "Relancer pour le paiement" — relance le client sur le solde restant d'une prestation déjà
    // clôturée (réutilise le même email facture, dont le corps s'adapte déjà au statut de paiement —
    // reste à payer/échéance — plutôt que d'inventer un nouveau type de message).
    const handleSendPaymentReminder = async () => {
        if (!appt.crm_email) {
            setReminderMessage({ type: 'error', text: 'Aucune adresse email renseignée pour ce client.' });
            return;
        }
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const invoice: any = await api(`/employe/prestation-planning/appointments/${appt.id}/invoice`);
            if (invoice?.error) { setReminderMessage({ type: 'error', text: invoice.error }); return; }
            const payload = invoice.payload_json || {};
            const snapshot = invoice.billing_snapshot || {};
            const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
            const { pdf } = await generateInvoicePdfFromData({ invoice, payload, snapshot });
            const dataUri: string = pdf.output('datauristring');
            const pdfBase64 = dataUri.split(',')[1];
            const res: any = await api(`/employe/prestation-planning/appointments/${appt.id}/send-invoice-email`, {
                method: 'POST',
                body: JSON.stringify({ to: appt.crm_email, pdfBase64, filename: `${invoice.invoice_number || 'facture'}.pdf` }),
            });
            if (res?.success) {
                setReminderMessage({ type: 'success', text: 'Relance envoyée.' });
                if (tab === 'emails') loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <User size={18} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-content truncate">{contactLabel(appt)}</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft shrink-0"><X size={18} /></button>
                </div>

                <div className="flex gap-1 bg-surface-soft border-b border-border p-2 shrink-0">
                    <button onClick={() => setTab('details')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'details' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        Détails
                    </button>
                    <button onClick={() => setTab('emails')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'emails' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        <MessageSquare size={12} /> Historique message
                    </button>
                </div>

                {tab === 'details' ? (
                    <div className="p-5 space-y-4 overflow-y-auto">
                        <div className="p-3 rounded-xl bg-surface-soft border border-border flex items-center justify-between">
                            <span className="text-xs text-content-muted flex items-center gap-1.5"><Clock size={13} /> Horaire</span>
                            <span className="font-bold text-sm text-content">{appt.start_time} - {endTime}</span>
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</h4>
                            {(appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{ label: appt.prestation_label, quantity: appt.quantity }]).map((item, idx) => (
                                <p key={idx} className="text-sm text-content">{item.label}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</p>
                            ))}
                        </div>
                        <div className="space-y-1.5 text-sm text-content">
                            {appt.crm_phone && <p className="flex items-center gap-2"><Phone size={13} className="text-content-muted shrink-0" /> {appt.crm_phone}</p>}
                            {appt.crm_email && <p className="flex items-center gap-2"><Mail size={13} className="text-content-muted shrink-0" /> {appt.crm_email}</p>}
                        </div>
                        <span className="inline-block w-fit text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-surface-soft border border-border text-content-muted">
                            {appt.location_type === 'entreprise' ? "Dans l'entreprise" : 'Chez le client'}
                        </span>
                        {fullAddress && (
                            <div className="space-y-2">
                                <p className="flex items-start gap-2 text-sm text-content"><MapPin size={13} className="text-content-muted shrink-0 mt-0.5" /> {fullAddress}</p>
                                {mapsUrl && (
                                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                                        <Navigation size={14} /> Ouvrir sur Maps
                                    </a>
                                )}
                            </div>
                        )}
                        {appt.notes && <p className="text-xs text-content-muted italic whitespace-pre-wrap">{appt.notes}</p>}
                        {appt.status === 'termine' && (
                            <div className="space-y-2">
                                <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Paiement</h4>
                                <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                    {(appt.payment_breakdown && appt.payment_breakdown.length > 0
                                        ? appt.payment_breakdown
                                        : (appt.payment_method ? [{ method: appt.payment_method, amount: Number(appt.amount_already_paid || 0) }] : [])
                                    ).map((entry, idx) => (
                                        <div key={idx} className="flex items-center justify-between">
                                            <span className="text-content-muted">{entry.method}</span>
                                            <span className="font-bold text-content">{fmt(entry.amount)}</span>
                                        </div>
                                    ))}
                                    {!appt.payment_method && (!appt.payment_breakdown || appt.payment_breakdown.length === 0) && (
                                        <p className="text-content-muted italic text-xs">Aucun règlement enregistré.</p>
                                    )}
                                    <div className="pt-1.5 border-t border-border flex items-center justify-between">
                                        <span className="text-content-muted">{isFullyPaid ? 'Total réglé' : 'Réglé à ce jour'}</span>
                                        <span className={`font-black ${isFullyPaid ? 'text-emerald-400' : 'text-content'}`}>{fmt(Number(appt.amount_already_paid || 0))}</span>
                                    </div>
                                    {appt.completed_at && (
                                        <p className="text-[11px] text-content-muted pt-0.5">
                                            Clôturé le {new Date(appt.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {!isClosed && (
                            <button onClick={handleResend} disabled={resending}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                <Send size={13} /> {resending ? 'Envoi...' : 'Renvoyer l\'email du rendez-vous au client'}
                            </button>
                        )}
                        {appt.crm_email && (
                            <button onClick={() => setShowSendMessage(true)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <Mail size={13} /> Message personnalisé
                            </button>
                        )}
                        {resendMessage && (
                            <p className={`text-xs font-semibold text-center ${resendMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{resendMessage.text}</p>
                        )}
                        {!isFullyPaid && (
                            <button onClick={isClosedUnpaid ? handleSendPaymentReminder : handleSendReminder} disabled={sendingReminder}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all ${isClosedUnpaid ? 'border-warning-500/30 bg-warning-500/10 text-warning-400 hover:bg-warning-500/20' : 'border-border text-content-muted hover:text-content hover:bg-surface-soft'}`}>
                                <Bell size={13} /> {sendingReminder ? 'Envoi...' : (isClosedUnpaid ? 'Relancer pour le paiement' : 'Envoyer un rappel de rendez-vous')}
                            </button>
                        )}
                        {reminderMessage && (
                            <p className={`text-xs font-semibold text-center ${reminderMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{reminderMessage.text}</p>
                        )}
                    </div>
                ) : (
                    <div className="p-5 space-y-2 overflow-y-auto">
                        {loadingEmails ? (
                            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                        ) : emails.length === 0 ? (
                            <p className="text-center text-sm text-content-muted italic py-8">Aucun email envoyé pour ce rendez-vous pour l'instant.</p>
                        ) : (
                            emails.map((e) => (
                                <div key={e.id} className="p-3 rounded-xl bg-surface-soft border border-border">
                                    <button onClick={() => setPreviewEmail(e)} className="w-full text-left">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold text-content">{EMAIL_TYPE_LABELS[e.email_type] || e.subject}</span>
                                            <span className="text-[10px] text-content-muted shrink-0 ml-2">{new Date(e.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <p className="text-[11px] text-content-muted mt-0.5">Envoyé à {e.recipient_email}</p>
                                    </button>
                                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border">
                                        <button onClick={() => handleResendEmail(e.id)} disabled={resendingEmailId === e.id}
                                            className="flex items-center gap-1 text-[11px] font-bold text-content-muted hover:text-content disabled:opacity-50 transition-all">
                                            <Send size={10} /> {resendingEmailId === e.id ? 'Envoi...' : 'Renvoyer'}
                                        </button>
                                        {resendEmailResult?.id === e.id && (
                                            <span className={`text-[11px] font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <div className="p-5 border-t border-border shrink-0">
                    {appt.status !== 'termine' ? (
                        <button onClick={onStartCompletion} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                            <CheckCircle size={16} /> Terminer la prestation
                        </button>
                    ) : (
                        <button onClick={onClose} className="w-full px-5 py-3 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Fermer</button>
                    )}
                </div>
            </motion.div>
            {showSendMessage && (
                <SendCustomMessageModal
                    recipientLabel={contactLabel(appt)}
                    tokens={[
                        { token: '{{prenom}}', desc: 'Prénom du client' },
                        { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
                        { token: '{{date}}', desc: 'Date du rendez-vous' },
                        { token: '{{heure}}', desc: 'Heure du rendez-vous' },
                        { token: '{{entreprise}}', desc: 'Votre établissement' },
                        { token: '{{logo}}', desc: 'Votre logo' },
                    ]}
                    onClose={() => setShowSendMessage(false)}
                    onSend={async (subject, body) => {
                        const res = await api(`/employe/prestation-planning/appointments/${appt.id}/send-message`, {
                            method: 'POST',
                            body: JSON.stringify({ subject, body }),
                        });
                        if (res?.success && tab === 'emails') loadEmails();
                        return res;
                    }}
                />
            )}
            {previewEmail && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-bold text-content text-sm truncate">{previewEmail.subject || EMAIL_TYPE_LABELS[previewEmail.email_type] || 'Email'}</h3>
                                <p className="text-xs text-content-muted mt-0.5 truncate">Envoyé à {previewEmail.recipient_email} le {new Date(previewEmail.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            <button onClick={() => setPreviewEmail(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto bg-surface-soft">
                            {previewEmail.html_body ? (
                                <div className="bg-white rounded-xl overflow-hidden border border-border">
                                    <iframe srcDoc={previewEmail.html_body} className="w-full" style={{ height: '55vh', border: 'none' }} sandbox="" title="Aperçu de l'email" />
                                </div>
                            ) : (
                                <p className="text-xs text-content-muted italic">Le contenu de cet email n'a pas été conservé (envoyé avant l'ajout de cet aperçu) — impossible de l'afficher ou de le renvoyer tel quel.</p>
                            )}
                        </div>
                        <div className="flex justify-between items-center gap-3 p-5 border-t border-border shrink-0">
                            {resendEmailResult?.id === previewEmail.id && (
                                <span className={`text-xs font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                            )}
                            <div className="flex gap-3 ml-auto">
                                <button onClick={() => setPreviewEmail(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Fermer</button>
                                {previewEmail.html_body && (
                                    <button onClick={() => handleResendEmail(previewEmail.id)} disabled={resendingEmailId === previewEmail.id}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                                        <Send size={14} /> {resendingEmailId === previewEmail.id ? 'Envoi...' : 'Renvoyer cet email'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CompletionWizard({ appt, onClose, onDone }: { appt: Appointment; onClose: () => void; onDone: () => void; }) {
    const [step, setStep] = useState<1 | 2>(1);
    const [hasExtras, setHasExtras] = useState<boolean | null>(null);
    const [extras, setExtras] = useState<ExtraLine[]>([]);
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [tvaRates, setTvaRates] = useState<number[]>([20]);
    const [paymentStatus, setPaymentStatus] = useState<'encaisse' | 'a_encaisser'>('a_encaisser');
    const [paymentEntries, setPaymentEntries] = useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    // Ce qui a déjà été réglé en ligne avant le rendez-vous (acompte + frais kilométriques éventuels) —
    // automatiquement déduit du reste à payer, jamais ressaisi. Distinct du paiement éventuellement encaissé
    // aujourd'hui (champ ci-dessous) pour que l'historique des paiements ne confonde jamais les deux.
    const alreadyPaidOnline = Math.max(0, Number(appt.amount_already_paid) || 0);
    const [newPaymentAmount, setNewPaymentAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api('/employe/prestation-catalog').then((d: any) => {
            setCatalog(Array.isArray(d?.catalog) ? d.catalog : []);
            setTvaRates(Array.isArray(d?.tva_rates) && d.tva_rates.length > 0 ? d.tva_rates : [20]);
        });
    }, []);

    const addCatalogExtra = (item: CatalogItem) => {
        setExtras((prev) => [...prev, { label: item.label, unit_price_ttc: item.unit_price_ttc, tax_rate: item.show_tva !== false ? item.tax_rate : 0, quantity: 1 }]);
    };
    const addBlankExtra = () => setExtras((prev) => [...prev, { label: '', unit_price_ttc: 0, tax_rate: tvaRates[0] ?? 20, quantity: 1 }]);
    const updateExtra = (idx: number, patch: Partial<ExtraLine>) => setExtras((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const removeExtra = (idx: number) => setExtras((prev) => prev.filter((_, i) => i !== idx));

    const baseTotal = appt.unit_price_ttc * appt.quantity;
    const validExtras = extras.filter((e) => e.label.trim());
    const extrasTotal = validExtras.reduce((sum, e) => sum + e.unit_price_ttc * e.quantity, 0);
    const grandTotal = baseTotal + extrasTotal;
    const remainingAfterOnline = Math.max(0, grandTotal - alreadyPaidOnline);
    const newMoneyToday = paymentStatus === 'encaisse' ? remainingAfterOnline : Math.min(remainingAfterOnline, Math.max(0, Number(newPaymentAmount || 0)));
    const alreadyPaidNum = Math.min(grandTotal, alreadyPaidOnline + newMoneyToday);
    const remainingDue = Math.max(0, grandTotal - alreadyPaidNum);
    const paymentMethodRequired = newMoneyToday > 0;
    const amountToAllocate = newMoneyToday;

    // Tant qu'un seul moyen de paiement est utilisé, son montant reste synchronisé automatiquement sur
    // le total à régler (le salarié n'a rien à saisir) — dès qu'un deuxième moyen est ajouté, chaque
    // montant devient éditable pour permettre une répartition (ex: 20€ espèces + le reste en virement).
    useEffect(() => {
        setPaymentEntries((prev) => prev.length <= 1 ? [{ method: prev[0]?.method || '', amount: amountToAllocate }] : prev);
    }, [amountToAllocate]);

    const updatePaymentEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setPaymentEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addPaymentEntry = () => {
        const allocated = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setPaymentEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((amountToAllocate - allocated) * 100) / 100) }]);
    };
    const removePaymentEntry = (idx: number) => setPaymentEntries((prev) => prev.filter((_, i) => i !== idx));
    const paymentEntriesSum = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const paymentEntriesValid = !paymentMethodRequired || (
        paymentEntries.every((e) => e.method && Number(e.amount) > 0) &&
        Math.abs(paymentEntriesSum - amountToAllocate) < 0.01
    );

    const handleValidate = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await api(`/employe/prestation-planning/appointments/${appt.id}/complete`, {
                method: 'POST',
                body: JSON.stringify({
                    extras: hasExtras ? validExtras : [],
                    payment_status: paymentStatus,
                    payment_entries: paymentMethodRequired ? paymentEntries : [],
                    new_payment_amount: newMoneyToday,
                }),
            });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }

            // L'email de fin de prestation part quoi qu'il arrive (généré côté navigateur avec jsPDF, pas
            // disponible côté serveur) — la facture PDF n'est jointe que si le module Facture est actif pour
            // cet établissement, auquel cas "invoice" est renseigné.
            if (res?.recipient_email) {
                try {
                    let pdfBase64: string | undefined, filename: string | undefined;
                    if (res.invoice) {
                        const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                        const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                        const dataUri: string = pdf.output('datauristring');
                        pdfBase64 = dataUri.split(',')[1];
                        filename = `${res.invoice?.invoice_number || 'facture'}.pdf`;
                    }
                    await api(`/employe/prestation-planning/appointments/${appt.id}/send-invoice-email`, {
                        method: 'POST',
                        body: JSON.stringify({ to: res.recipient_email, pdfBase64, filename }),
                    });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice email', pdfErr);
                }
            }

            onDone();
        } catch (e: any) {
            setError('Erreur lors de la clôture');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><CheckCircle size={18} className="text-emerald-400" /> Terminer la prestation</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {step === 1 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Y a-t-il eu des extras pendant le rendez-vous ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setHasExtras(false)} className={pillClass(hasExtras === false)}>Non</button>
                                <button onClick={() => setHasExtras(true)} className={pillClass(hasExtras === true)}>Oui</button>
                            </div>
                            {hasExtras && (
                                <div className="space-y-3">
                                    {catalog.length > 0 && (
                                        <select
                                            onChange={(e) => { const item = catalog.find((c) => c.id === e.target.value); if (item) addCatalogExtra(item); e.target.value = ''; }}
                                            defaultValue=""
                                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
                                        >
                                            <option value="">+ Ajouter depuis le catalogue...</option>
                                            {catalog.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>
                                    )}
                                    <button type="button" onClick={addBlankExtra} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Saisie libre d'une ligne
                                    </button>
                                    {extras.map((ex, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-surface-soft border border-border rounded-xl p-2">
                                            <input value={ex.label} onChange={(e) => updateExtra(idx, { label: e.target.value })} placeholder="Description"
                                                className="flex-1 min-w-0 bg-transparent text-sm text-content outline-none" />
                                            <input type="number" min={1} step="1" value={ex.quantity}
                                                onChange={(e) => updateExtra(idx, { quantity: Math.max(1, Number(e.target.value || 1)) })}
                                                placeholder="Qté" title="Quantité"
                                                className="w-12 bg-transparent text-sm text-content outline-none text-right" />
                                            <input type="number" min={0} step="0.01" value={ex.unit_price_ttc}
                                                onChange={(e) => updateExtra(idx, { unit_price_ttc: Number(e.target.value || 0) })}
                                                placeholder="TTC" title="Montant TTC unitaire"
                                                className="w-16 bg-transparent text-sm text-content outline-none text-right" />
                                            <select value={ex.tax_rate} onChange={(e) => updateExtra(idx, { tax_rate: Number(e.target.value) })}
                                                title="TVA" className="shrink-0 bg-transparent text-xs text-content-muted outline-none">
                                                {(tvaRates.includes(ex.tax_rate) ? tvaRates : [...tvaRates, ex.tax_rate]).map((r) => (
                                                    <option key={r} value={r}>{r}%</option>
                                                ))}
                                            </select>
                                            <button type="button" onClick={() => removeExtra(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-content font-semibold">La prestation a-t-elle été payée ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setPaymentStatus('encaisse')} className={pillClass(paymentStatus === 'encaisse')}>ENCAISSER</button>
                                <button onClick={() => setPaymentStatus('a_encaisser')} className={pillClass(paymentStatus === 'a_encaisser')}>À ENCAISSER</button>
                            </div>
                            {alreadyPaidOnline > 0 && (
                                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-sm">
                                    <span className="text-emerald-400 font-semibold">Déjà réglé en ligne</span>
                                    <span className="font-bold text-emerald-400">{fmt(alreadyPaidOnline)}</span>
                                </div>
                            )}
                            {paymentStatus === 'a_encaisser' && remainingAfterOnline > 0 && (
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Montant supplémentaire encaissé aujourd'hui (optionnel)</label>
                                    <input type="number" min={0} step="0.01" value={newPaymentAmount}
                                        onChange={(e) => setNewPaymentAmount(e.target.value)}
                                        placeholder="Ex: 50"
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            )}
                            {paymentMethodRequired && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Moyen de paiement (pour le montant encaissé aujourd'hui)</p>
                                    {paymentEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface-soft border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updatePaymentEntry(idx, { method: m })}
                                                        className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${entry.method === m ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {paymentEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updatePaymentEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removePaymentEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addPaymentEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {paymentEntries.length > 1 && Math.abs(paymentEntriesSum - amountToAllocate) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({fmt(paymentEntriesSum)}) ne correspond pas au montant à régler ({fmt(amountToAllocate)}).
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="p-4 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                <div className="flex justify-between text-content-muted">
                                    <span>{appt.prestation_label}{appt.quantity > 1 ? ` × ${appt.quantity}` : ''}</span>
                                    <span>{fmt(baseTotal)}</span>
                                </div>
                                {validExtras.map((e, i) => (
                                    <div key={i} className="flex justify-between text-content-muted">
                                        <span>{e.label}{e.quantity > 1 ? ` × ${e.quantity}` : ''}</span>
                                        <span>{fmt(e.unit_price_ttc * e.quantity)}</span>
                                    </div>
                                ))}
                                {paymentStatus === 'a_encaisser' && alreadyPaidNum > 0 && (
                                    <div className="flex justify-between text-content-muted">
                                        <span>Déjà réglé</span>
                                        <span>- {fmt(alreadyPaidNum)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-border font-bold text-content">
                                    <span>{paymentStatus === 'encaisse' ? 'TOTAL ENCAISSÉ' : 'TOTAL NET À PAYER'}</span>
                                    <span className={paymentStatus === 'encaisse' ? 'text-emerald-400' : 'text-red-400'}>{fmt(paymentStatus === 'encaisse' ? grandTotal : remainingDue)}</span>
                                </div>
                            </div>
                        </>
                    )}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-between items-center p-5 border-t border-border shrink-0">
                    <button onClick={step === 1 ? onClose : () => setStep(1)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">
                        {step === 1 ? 'Annuler' : 'Retour'}
                    </button>
                    {step === 1 ? (
                        <button onClick={() => setStep(2)} disabled={hasExtras === null} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">Suivant</button>
                    ) : (
                        <button onClick={handleValidate} disabled={submitting || !paymentEntriesValid} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                            {submitting ? 'Validation...' : 'Valider'}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
}

export const PrestationEmployeePlanning: React.FC = () => {
    const [date, setDate] = useState(formatDateInput(new Date()));
    const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
    const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [weekAppointments, setWeekAppointments] = useState<Record<string, Appointment[]>>({});
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<Appointment | null>(null);
    const [showWizard, setShowWizard] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [settings, emps] = await Promise.all([
                    api('/employe/prestation-planning/settings'),
                    api('/employes'),
                ]);
                if (settings?.weekly_hours) setWeeklyHours({ ...defaultWeeklyHours(), ...settings.weekly_hours });
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
            } catch (e) {
                console.error('Failed to load prestation planning setup', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const loadDay = async (d: string) => {
        try {
            const rows = await api(`/employe/prestation-planning/day?date=${d}`);
            setAppointments(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setAppointments([]);
        }
    };

    const weekDates = useMemo(() => {
        const d = new Date(date + 'T00:00:00');
        const day = d.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMonday);
        const dates: string[] = [];
        for (let i = 0; i < 7; i++) {
            const dd = new Date(monday);
            dd.setDate(monday.getDate() + i);
            dates.push(formatDateInput(dd));
        }
        return dates;
    }, [date]);

    const loadWeek = async (dates: string[]) => {
        try {
            const results = await Promise.all(dates.map((d) => api(`/employe/prestation-planning/day?date=${d}`)));
            const map: Record<string, Appointment[]> = {};
            dates.forEach((d, i) => { map[d] = Array.isArray(results[i]) ? results[i] : []; });
            setWeekAppointments(map);
        } catch (e) {
            console.error('Failed to load week appointments', e);
        }
    };

    const reloadCurrentView = async () => {
        if (viewMode === 'day') await loadDay(date);
        else await loadWeek(weekDates);
    };

    useEffect(() => {
        if (viewMode === 'day') loadDay(date);
        else loadWeek(weekDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, viewMode]);

    const shiftPeriod = (direction: 1 | -1) => {
        const deltaDays = (viewMode === 'week' ? 7 : 1) * direction;
        const d = new Date(date + 'T00:00:00');
        d.setDate(d.getDate() + deltaDays);
        setDate(formatDateInput(d));
    };

    const dayKey = dayKeyForDate(date);
    const dayHours = weeklyHours[dayKey];
    const isDayClosed = dayHours.closed;
    const openingMinutes = timeToMinutes(dayHours.opening);
    const closingMinutes = timeToMinutes(dayHours.closing);

    const weekAxis = useMemo(() => {
        const openDays = weekDates.map((d) => weeklyHours[dayKeyForDate(d)]).filter((h) => !h.closed);
        if (openDays.length === 0) return { opening: timeToMinutes('08:00'), closing: timeToMinutes('20:00') };
        const opening = Math.min(...openDays.map((h) => timeToMinutes(h.opening)));
        const closing = Math.max(...openDays.map((h) => timeToMinutes(h.closing)));
        return { opening, closing: Math.max(closing, opening + 60) };
    }, [weekDates, weeklyHours]);

    const axisOpeningMinutes = viewMode === 'day' ? openingMinutes : weekAxis.opening;
    const axisClosingMinutes = viewMode === 'day' ? closingMinutes : weekAxis.closing;
    const axisTotalMinutes = Math.max(60, axisClosingMinutes - axisOpeningMinutes);
    const axisTotalHeight = (axisTotalMinutes / 60) * PX_PER_HOUR;

    const hourMarks = useMemo(() => {
        const marks: number[] = [];
        for (let m = axisOpeningMinutes; m <= axisClosingMinutes; m += 60) marks.push(m);
        return marks;
    }, [axisOpeningMinutes, axisClosingMinutes]);

    const weekRangeLabel = () => {
        const start = new Date(weekDates[0] + 'T00:00:00');
        const end = new Date(weekDates[6] + 'T00:00:00');
        const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: start.getMonth() === end.getMonth() ? undefined : 'long' });
        const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${startStr} – ${endStr}`;
    };

    const refreshDetail = (id: string) => {
        const pool = viewMode === 'day' ? appointments : Object.values(weekAppointments).flat();
        return pool.find((a) => a.id === id) || null;
    };

    const handleCompletionDone = async () => {
        setShowWizard(false);
        setDetail(null);
        await reloadCurrentView();
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du planning...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-content">Mon Planning</h1>
                    <p className="text-sm text-content-muted mt-1 capitalize">{viewMode === 'day' ? formatDateHuman(date) : weekRangeLabel()}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-surface-soft border border-border rounded-xl p-1">
                        <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Semaine</button>
                        <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Jour</button>
                    </div>
                    <button onClick={() => shiftPeriod(-1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronLeft size={16} /></button>
                    <input type="date" value={viewMode === 'week' ? weekDates[0] : date} onChange={(e) => setDate(e.target.value)}
                        className="bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    <button onClick={() => shiftPeriod(1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronRight size={16} /></button>
                    <button onClick={() => setDate(formatDateInput(new Date()))} className="px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold hover:bg-surface-soft transition-all">Aujourd'hui</button>
                </div>
            </header>


            {viewMode === 'day' && isDayClosed ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Fermé le {DAY_LABELS[dayKey].toLowerCase()}.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="flex min-w-max">
                            <div className="w-16 shrink-0 border-r border-border">
                                <div className="h-12 border-b border-border" />
                                <div className="relative" style={{ height: axisTotalHeight }}>
                                    {hourMarks.map((m) => (
                                        <div key={m} className="absolute left-0 right-0 text-[10px] text-content-muted px-1 -translate-y-1/2"
                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }}>
                                            {minutesToTime(m)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(viewMode === 'day' ? [date] : weekDates).map((d) => {
                                const dHours = weeklyHours[dayKeyForDate(d)];
                                const dAppointments = viewMode === 'day' ? appointments : (weekAppointments[d] || []);
                                const { packed, laneCount } = packAppointments(dAppointments);
                                return (
                                    <div key={d} className={viewMode === 'day' ? 'flex-1 min-w-[320px] border-r border-border last:border-r-0' : 'flex-1 min-w-[150px] border-r border-border last:border-r-0'}>
                                        <div className="h-12 border-b border-border flex items-center justify-center px-2">
                                            <span className="text-xs font-bold text-content truncate">{viewMode === 'day' ? formatDateHuman(d) : dayShortLabel(d)}</span>
                                        </div>
                                        <div className="relative" style={{ height: axisTotalHeight }}>
                                            {dHours.closed ? (
                                                <div className="absolute inset-0 flex items-center justify-center text-[11px] text-content-muted italic">Fermé</div>
                                            ) : dAppointments.length === 0 ? (
                                                hourMarks.map((m) => (
                                                    <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                        style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                ))
                                            ) : (
                                                <>
                                                    {hourMarks.map((m) => (
                                                        <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                    ))}
                                                    {packed.map(({ appt, lane }) => {
                                                        const startMin = timeToMinutes(appt.start_time);
                                                        const endTime = minutesToTime(startMin + Math.round(appt.computed_duration_minutes));
                                                        const top = Math.max(0, ((startMin - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight);
                                                        const minHeight = Math.max(28, (appt.computed_duration_minutes / axisTotalMinutes) * axisTotalHeight);
                                                        const width = 100 / laneCount;
                                                        const left = lane * width;
                                                        return (
                                                            <button
                                                                key={appt.id}
                                                                type="button"
                                                                onClick={() => setDetail(appt)}
                                                                className="absolute z-10 hover:z-20 rounded-lg bg-brand-600 border border-brand-700 hover:bg-brand-500 shadow-sm transition-all px-1.5 py-1 text-left"
                                                                style={{ top, minHeight, left: `${left}%`, width: `calc(${width}% - 4px)` }}
                                                            >
                                                                <p className="text-[10px] font-bold text-white leading-snug break-words">{contactLabel(appt)}</p>
                                                                <p className="text-[9px] text-white/80 leading-snug">{appt.start_time} - {endTime}</p>
                                                                <p className="text-[9px] text-white/80 leading-snug break-words">{appt.prestation_label}</p>
                                                                <p className="text-[9px] text-white/90 font-semibold leading-snug break-words">{employeeNamesLabel(appt, employees)}</p>
                                                                {appt.status === 'termine' && (
                                                                    <p className="text-[9px] font-bold text-emerald-300 leading-snug">Terminé</p>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {detail && !showWizard && (
                    <DetailModal appt={refreshDetail(detail.id) || detail} onClose={() => setDetail(null)} onStartCompletion={() => setShowWizard(true)} />
                )}
                {detail && showWizard && (
                    <CompletionWizard appt={detail} onClose={() => setShowWizard(false)} onDone={handleCompletionDone} />
                )}
            </AnimatePresence>
        </div>
    );
};
